import { DateTimeUtils } from "@common/utils";
import { DateTime } from "luxon";
import { VeterinarianListingModel } from '../';
import {
    DefaultEntitySchedulePreference, HospitalScheduleDayModel, HospitalScheduleDayEntityModel, HospitalScheduleVeterinarianModel, HospitalScheduleVetTechModel, ScheduleHospitalScheduleTemplateListingModel,
    EntityOverrideSchedulePreference, EntitySchedulePreference, ScheduleEntityTypeModel
} from './';
import { HospitalScheduleWrapperModel } from "./HospitalScheduleWrapperModel";
import { EntityScheduleType, WeekDay } from "../Enums";
import { first, flatMap, some } from "lodash";

/**
 * The outermost model for GetHospitalSchedule, which contains (or will contain) the entire payload for a schedule request
 */
export class ProcessedHospitalScheduleModel {
    /**
     * The start date for the requested schedule range
     */
    startDate: DateTime;
    /**
     * The end date for the requested schedule range
     */
    endDate: DateTime;
    /**
     * The Veterinarian Listing Model's for any vets on the schedule for the requested date range, or that have access to any hospitals the schedule was requested for.  
     * If a vet isn't scheduled and doesn't have access to one of the requested hospitals, they won't be in this list.
     */
    veterinarians: HospitalScheduleVeterinarianModel[];
    /**
     * The Veterinarian Listing Model's for any vets on the schedule for the requested date range, or that have access to any hospitals the schedule was requested for.  
     * If a vet isn't scheduled and doesn't have access to one of the requested hospitals, they won't be in this list.
     */
    vetTechs: HospitalScheduleVetTechModel[];
    /**
     * The templates scheduled at any hospitals the schedule was requested for.  
     * Also, default templates for hospitals the schedule was requested for are included here whether they were scheduled or not.  
     * This is so we have the default schedule preferences to give to vets that don't have any.  
     */
    templates: ScheduleHospitalScheduleTemplateListingModel[];


    /**
     * A schedule wrapper for each hospital the schedule was requested for.  If only 1 hospital was requested there will only be 1 entry in this array.
     */
    hospitalSchedules: HospitalScheduleWrapperModel[];
    /**
     * The combined, unprocessed (non computed) vet and schedule template schedule preferences, use entityType to tell the difference.
     * These are needed for show overrides, and future initiatives to remove schedule v1 and redundant preference calls in the app     
     */
    entitySchedulePreferences: EntitySchedulePreference[];
    /**
     * The combined, unprocessed (non computed) vet and schedule template override schedule preferences.
     */
    entityOverrideSchedulePreferences: EntityOverrideSchedulePreference[];

    constructor(init?: Partial<ProcessedHospitalScheduleModel>) {
        this.startDate = DateTimeUtils.getAsDateTimeUTC(init?.startDate ?? DateTime.utc())!;
        this.endDate = DateTimeUtils.getAsDateTimeUTC(init?.endDate ?? DateTime.utc())!;
        this.veterinarians = init?.veterinarians?.map(sv => new HospitalScheduleVeterinarianModel(sv)) ?? [] as HospitalScheduleVeterinarianModel[];
        this.vetTechs = init?.vetTechs?.map(sv => new HospitalScheduleVetTechModel(sv)) ?? [] as HospitalScheduleVetTechModel[];

        this.templates = init?.templates?.map(t => new ScheduleHospitalScheduleTemplateListingModel(t)) ?? [] as ScheduleHospitalScheduleTemplateListingModel[];
        this.hospitalSchedules = init?.hospitalSchedules?.map(t => new HospitalScheduleWrapperModel(t)) ?? [new HospitalScheduleWrapperModel({ hospitalId: 0, schedule: [new HospitalScheduleDayModel()] })] as HospitalScheduleWrapperModel[];

        //unprocessed preferences (does not include preferences for adhoc vets/templates unless @includeAdHocPreferences is 1 to the stored procedure, not currently implemented as it's not needed as of now)
        this.entitySchedulePreferences = init?.entitySchedulePreferences?.map(t => new EntitySchedulePreference(t)) ?? [] as EntitySchedulePreference[];
        this.entityOverrideSchedulePreferences = init?.entityOverrideSchedulePreferences?.map(t => new EntityOverrideSchedulePreference(t)) ?? [] as EntityOverrideSchedulePreference[];
    }

    /**
     * Will grab the vet listing model or scheduleHospitalScheduleTemplateListingModel for the supplied entity, if it's a vet entity vet model, if it's a template entity, template model, cast the result as needed
     * @param entity 
     * @returns 
     */
    getScheduledEntity(entity: HospitalScheduleDayEntityModel): VeterinarianListingModel | ScheduleHospitalScheduleTemplateListingModel | ScheduleEntityTypeModel | undefined {
        switch (entity.entityType) {
            case EntityScheduleType.Template:
                return first(this.templates.filter(t => t.hospitalScheduleTemplateId === entity.entityId));
            case EntityScheduleType.Veterinarian:
                return first(this.veterinarians.filter(t => t.veterinarianId === entity.entityId));
            case EntityScheduleType.Technician:
                return new ScheduleEntityTypeModel({
                    scheduleEntityTypeId: 400,
                    scheduleEntityTypeName: 'Technician',
                    legacyEntityTypeId: 400,
                    businessLineId: 3,
                    isGloballyScheduled: true
                });
            case EntityScheduleType.Unassigned:
                return new ScheduleEntityTypeModel({
                    scheduleEntityTypeId: 200,
                    scheduleEntityTypeName: 'Unassigned',
                    legacyEntityTypeId: 200,
                    businessLineId: 3,
                    isGloballyScheduled: true
                });
            default:
                return undefined;
        }
    }

    getScheduleEntitiesForAllDays(hospitalId: number) {

        return flatMap(this.hospitalSchedules.filter(h => h.hospitalId === hospitalId), h => flatMap(h.schedule, s => s.scheduledEntities));
    }

    /**
     * Returns all the veterinarians for the specified options from the schedule payload.  Available veterinarians are vets that are on at least 1 schedule entry, and or have access to the requested hospital or hospitals.
     * @param hospitalId The id of the hospital to filter vets down to.
     * @param options Options that further filter the vets being returned, day is special, if you leave it undefined it'll assume you don't know the day yet (not loaded, or doesn't have a schedule that day) so it'll cause isScheduled to always return no vets if it's undefined.
     * isScheduled undefined means vets are included regardless of whether they are scheduled or not.  true (only scheduled vets), false (only unscheduled vets)
     * hasAccess undefined means vets are included regardless of whether they have access to the requested hospital, true (only vets that have access), false (only vets that don't have access)
     * @returns 
     */
    getVeterinarians(hospitalId: number, options: { day?: DateTime | string | undefined, isAdhoc?: boolean | undefined, isScheduled?: boolean | undefined, hasLicense?: boolean | undefined, hasAccess?: boolean | undefined }) {
        const lday = options?.day
            ? typeof (options?.day) === 'string'
                ? DateTime.fromISO(options?.day, { zone: 'UTC' }).startOf('day')
                : options?.day.startOf('day')
            : DateTime.utc().startOf('day');

        const hospitalSchedule = first(this.hospitalSchedules.filter(h => h.hospitalId == hospitalId));
        const hospitalScheduleDays = hospitalSchedule?.schedule.filter(s => {
            return lday.toISO() === s.day.startOf('day').toISO()
        }) ?? [];
        const totalScheduledEntities: HospitalScheduleDayEntityModel[] = [];
        hospitalScheduleDays.forEach(scheduleDay => {
            scheduleDay.scheduledEntities.filter(
                e => e.entityType === EntityScheduleType.Veterinarian
            )
                .forEach(day => totalScheduledEntities.push(day))
        });

        if (!options?.day && options?.isScheduled)
            throw 'Unable to filter vets to only sheduled templates because no day was specified.  When filtering on scheduled you must specify an options day.';

        return this.veterinarians.filter(v => {
            let includeOnScheduled = () => (options?.isScheduled == null || some(totalScheduledEntities, tse => tse.entityId === v.veterinarianId) === options.isScheduled);
            let includeOnLicense = () => (options?.hasLicense == null || v.isLicensed(hospitalId));
            let includeOnAccess = () => (options?.hasAccess == null || v.hasAccess(hospitalId))
            let includeOnAdHoc = () => (options?.isAdhoc == null || some(totalScheduledEntities, tse => tse.entityId === v.veterinarianId && tse.isAdhoc === options.isAdhoc));
            let activeOnly = () => (v.employmentStatus === 'A')
            return includeOnScheduled() && includeOnLicense() && includeOnAccess() && includeOnAdHoc() && activeOnly();
        });
    }

    /**
     * Returns all the templates for the specified options from the schedule payload.  You can filter down with options to include only default or non default templates, active or non active templates, etc.
     * Undefined options mean the otions are ignored and don't affect the result.
     * @param hospitalId 
     * @param options 
     * @returns 
     */
    getTemplates(hospitalId: number, options: { day?: DateTime | string | undefined, isScheduled?: boolean | undefined, isActive?: boolean | undefined, isDefault?: boolean | undefined }) {
        const lday = options?.day ? typeof (options?.day) === 'string' ? DateTime.fromISO(options?.day, { zone: 'UTC' }).startOf('day') : options?.day.startOf('day') : DateTime.utc().startOf('day');
        const hospitalSchedule = first(this.hospitalSchedules.filter(h => h.hospitalId == hospitalId));
        const hospitalScheduleDays = hospitalSchedule?.schedule.filter(s => lday.toISO() === s.day.startOf('day').toISO()) ?? [];
        const totalScheduledEntities: HospitalScheduleDayEntityModel[] = [];
        hospitalScheduleDays.forEach(scheduleDay => {
            scheduleDay.scheduledEntities.filter(
                e => e.entityType === EntityScheduleType.Template
            )
                .forEach(day => totalScheduledEntities.push(day));
        });
        if (!options?.day && options?.isScheduled) {
            throw 'Unable to filter templates to only sheduled templates because no day was specified.  When filtering on scheduled you must specify an options day.';
        }

        return this.templates.filter(t =>
            (options?.isScheduled == null || some(totalScheduledEntities, tse => tse.entityId === t.hospitalScheduleTemplateId) === options.isScheduled)
            && (options?.isDefault == null || t.isDefault === options?.isDefault)
            && (options?.isActive == null || t.isActive === options?.isActive));
    }

    /**
     * Takes a schedule entity which might be for a vet or a template, and gets copies of all the default schedule preferences from the EntitySchedulePreferences array
     * also modifies the schedule preferences entity id to point at the passed in entity instead of the original template id.  This is used to render
     * default schedule preferences for vets on scheduling calendar modals.
     * The preferences for the default template are already in the base payload, so we look them up in it to prevent redundantly nesting the json data in the response.
     * @param entity 
     * @returns 
     */
    createDefaultEntitySchedulePreferences(entity: HospitalScheduleDayEntityModel, dayOfWeek?: WeekDay.Day | undefined) {
        if (entity.defaultScheduleTemplateId == null)
            return [] as EntitySchedulePreference[];
        return this.entitySchedulePreferences.filter(p => p.entityType === EntityScheduleType.Template && p.entityId === entity.defaultScheduleTemplateId && (dayOfWeek == null || dayOfWeek == p.dayOfWeek))
            .map(p => {
                return new DefaultEntitySchedulePreference({ ...p as DefaultEntitySchedulePreference, entityId: entity.entityId, entityType: EntityScheduleType.Veterinarian, inheritedFromTemplate: true, templateId: entity.defaultScheduleTemplateId! })
            });
    }

    getEntitySchedulePreferences(entity: HospitalScheduleDayEntityModel, dayOfWeek?: WeekDay.Day | undefined) {
        if (!entity.hasSchedulePreferences)
            return entity.hasInheritedSchedulePreferences && entity.defaultScheduleTemplateId != null ? this.createDefaultEntitySchedulePreferences(entity, dayOfWeek) : [] as EntitySchedulePreference[];
        return this.entitySchedulePreferences.filter(p => p.entityType === entity.entityType && p.entityId === entity.entityId && (dayOfWeek == null || dayOfWeek === p.dayOfWeek));
    }

    getFirstScheduleDay(hospitalId: number) {
        return first(first(this.hospitalSchedules.filter(h => h.hospitalId === hospitalId))?.schedule);
    }

    static getDefault() {
        const daySchedule = new HospitalScheduleDayModel({ hospitalId: 0 });
        const wrapper = new HospitalScheduleWrapperModel({ hospitalId: 0, schedule: [daySchedule] });
        return new ProcessedHospitalScheduleModel({
            hospitalSchedules: [wrapper]
        });
    }
}
